/**
 * useProducts hook - Fetches products from backend API with pagination support
 *
 * FE-004: Replace static PRODUCTS array with API calls
 * FE-PERF-006: Added server-side pagination support
 *
 * Performance & Stability Improvements:
 * - Uses AbortController to cancel requests on unmount (prevents memory leaks)
 * - Server-side pagination to reduce initial load time and memory usage
 * - Proper cleanup on component unmount
 * - Falls back to empty array on error to prevent UI crashes
 *
 * This hook provides:
 * - products: Array of products from API (current page)
 * - loading: Boolean loading state
 * - error: Error message if fetch failed
 * - refetch: Function to manually refetch products
 * - pagination: Object with page, perPage, total, totalPages, hasNextPage, hasPrevPage
 * - goToPage: Function to navigate to specific page
 * - nextPage: Function to go to next page
 * - prevPage: Function to go to previous page
 * - changePerPage: Function to change items per page
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { productsAPI } from '../services/api';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 24;

/**
 * Transform backend product data to frontend format
 */
function transformProduct(product) {
  const placeholder = 'https://placehold.co/400x500/27272a/f59e0b?text=' + encodeURIComponent(product.name?.charAt(0) || '?');
  return {
    id: product.id,
    name: product.name,
    category: product.category || 'comics',
    subcategory: product.subcategory || product.category || 'Other',
    price: parseFloat(product.price) || 0,
    originalPrice: product.original_price ? parseFloat(product.original_price) : null,
    image: product.image_url || product.image || placeholder,
    image_url: product.image_url,
    images: product.images || [],
    description: product.description || '',
    stock: product.stock ?? 0,
    featured: product.featured ?? false,
    rating: product.rating ?? 4.0,
    tags: product.tags || [],
    sku: product.sku,
    upc: product.upc,
    // Dimensions
    interior_width: product.interior_width,
    interior_height: product.interior_height,
    interior_length: product.interior_length,
    exterior_width: product.exterior_width,
    exterior_height: product.exterior_height,
    exterior_length: product.exterior_length,
    weight: product.weight,
    material: product.material
  };
}

/**
 * @param {Object} options - Configuration options
 * @param {number} options.initialPage - Starting page (default: 1)
 * @param {number} options.initialPerPage - Items per page (default: 24)
 * @param {string} options.category - Filter by category
 * @param {string} options.sort - Sort order
 */
export function useProducts(options = {}) {
  const {
    initialPage = DEFAULT_PAGE,
    initialPerPage = DEFAULT_PER_PAGE,
    category = null,
    sort = null,
  } = options;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(initialPage);
  const [perPage, setPerPage] = useState(initialPerPage);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Track if component is mounted to prevent state updates after unmount
  const mountedRef = useRef(true);
  // Track current abort controller for cleanup
  const abortControllerRef = useRef(null);

  const fetchProducts = useCallback(async (fetchPage = page, fetchPerPage = perPage) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError(null);

    try {
      const data = await productsAPI.getAll({
        page: fetchPage,
        per_page: fetchPerPage,
        category: category,
        sort: sort,
        signal: abortController.signal
      });

      // Only update state if component is still mounted and request wasn't aborted
      if (!mountedRef.current || abortController.signal.aborted) {
        return;
      }

      // API returns { products: [], total, page, per_page }
      const productsList = Array.isArray(data) ? data : (data.products || []);
      const transformed = productsList.map(transformProduct);

      // Extract pagination metadata from response
      const responseTotal = data.total || productsList.length;
      const responsePerPage = data.per_page || fetchPerPage;
      const responseTotalPages = Math.ceil(responseTotal / responsePerPage);

      // DEBUG: Log pagination info in development
      if (import.meta.env.DEV) {
        console.log('[useProducts] Fetched page', fetchPage, 'of', responseTotalPages, '- Total:', responseTotal);
      }

      setProducts(transformed);
      setTotal(responseTotal);
      setTotalPages(responseTotalPages);
      setPage(fetchPage);
      setPerPage(responsePerPage);
      setError(null);
    } catch (err) {
      // Ignore abort errors - they're intentional
      if (err.name === 'AbortError') {
        return;
      }

      // Only update state if component is still mounted
      if (!mountedRef.current) {
        return;
      }

      console.error('Failed to fetch products:', err);
      setError(err.message || 'Failed to load products');
      setProducts([]);
    } finally {
      // Only update loading state if component is still mounted
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [page, perPage, category, sort]);

  useEffect(() => {
    mountedRef.current = true;
    fetchProducts();

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchProducts]);

  // Pagination navigation functions
  const goToPage = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  }, [totalPages]);

  const nextPage = useCallback(() => {
    if (page < totalPages) {
      setPage(p => p + 1);
    }
  }, [page, totalPages]);

  const prevPage = useCallback(() => {
    if (page > 1) {
      setPage(p => p - 1);
    }
  }, [page]);

  const changePerPage = useCallback((newPerPage) => {
    setPerPage(newPerPage);
    setPage(1); // Reset to first page when changing page size
  }, []);

  return {
    products,
    loading,
    error,
    refetch: () => fetchProducts(page, perPage),
    // Pagination data and controls
    pagination: {
      page,
      perPage,
      total,
      totalPages,
      hasNextPage: page < totalPages,
      hasPrevPage: page > 1,
    },
    goToPage,
    nextPage,
    prevPage,
    changePerPage,
  };
}

export default useProducts;
